import PropTypes from 'prop-types';
import React from 'react';

import {assert} from 'Asserts';
import * as FileConstants from 'folder-items/FileConstants';
import {TargetEntry} from 'folder-items/FileConstants';
import {EntrySummary} from 'notebook/ELNConstants';
import EntrySearchInput from 'notebook/entries/EntrySearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import {ElementKeyboardEvent} from 'util/Types';

export interface EntryLinkInputProps {
  areChipsClickable?: boolean;
  autoFocus: boolean;
  chipSize?: ChipSize;
  defaultValue: FileConstants.SchemaTag;
  onBlur?: () => void;
  onChange: (newValue: {targetEntries: Array<Partial<FileConstants.TargetEntry>>; value: string}) => void;
  onKeyDown?: (element: ElementKeyboardEvent) => void;
  query?: string;
  onUpdateQuery?: (query: string) => void;
  placeholder?: string;
}

export default function EntryLinkInput(props: EntryLinkInputProps): JSX.Element {
  const {tagSchemaField, targetEntries, value} = props.defaultValue;
  assert(targetEntries != null, 'Expected an entry link schema tag');
  return (
    <EntrySearchInput
      areChipsClickable={props.areChipsClickable}
      autoFocus={props.autoFocus}
      chipSize={props.chipSize}
      // Similar to other tag input components (e.g., BioentityLinkInput),
      // since all entry links already have a text representation in the 'value' key,
      // we only populate the initial query if there are no links already.
      initialQuery={targetEntries.length === 0 ? value || undefined : ''}
      onBlur={props.onBlur}
      onChange={(entries: Array<EntrySummary | TargetEntry>) => {
        props.onChange({
          targetEntries: entries.map((entry) => ({
            id: entry.id,
            name: entry.name,
            // eslint-disable-next-line benchlint/no-in-operator
            url: 'editURL' in entry ? entry.editURL : undefined,
            // eslint-disable-next-line benchlint/no-in-operator
            humanId: 'humanId' in entry ? entry.humanId : null,
          })),
          value: '',
        });
      }}
      onKeyDown={props.onKeyDown}
      selectedEntries={targetEntries}
      shouldAllowMultiple={tagSchemaField.isMulti}
      // This component can optionally be controlled. See ItemSearchInput for where the magic happens.
      query={props.query}
      onUpdateQuery={props.onUpdateQuery}
      placeholder={props.placeholder}
    />
  );
}

EntryLinkInput.propTypes = {
  areChipsClickable: PropTypes.bool,
  autoFocus: PropTypes.bool.isRequired,
  chipSize: PropTypes.oneOf(Object.values(ChipSize)),
  defaultValue: FileConstants.TAG_SHAPE.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  query: PropTypes.string,
  onUpdateQuery: PropTypes.func,
  placeholder: PropTypes.string,
};
